import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { Provider, ProviderType, TrackingEvent } from '../types';

interface TtdPixelConfig {
  advertiserId: string;
  trackingTagIds: string[];
  trackingUrl?: string;
  isEnabled?: boolean;
}

// Define TTD Universal Pixel API interface
interface TTDUniversalPixelApi {
  init: (advertiserId: string, trackingTagIds: string[], trackingUrl: string) => void;
  track: (eventName: string, data?: any) => void;
  // Add other methods as needed
}

// Window interface已在index.d.ts中统一定义

export class TtdPixelProvider implements Provider {
  type = ProviderType.TTD_PIXEL;
  isEnabled: boolean;
  loggingEnabled = false; // 公开属性，支持ReportingCenter访问
  private initialized = false;
  private config: TtdPixelConfig;
  private pixelApi: TTDUniversalPixelApi | null = null;

  constructor(config: TtdPixelConfig) {
    this.config = {
      ...config,
      trackingUrl: config.trackingUrl || 'https://insight.adsrvr.org/track/up',
    };
    this.isEnabled = config.isEnabled ?? true;
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  async init(): Promise<boolean> {
    try {
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;

      // Load TTD Universal Pixel script
      await this.loadTtdScript();

      // Wait for DOM ready and initialize
      return new Promise((resolve) => {
        this.waitForTtdReady(() => {
          try {
            if (typeof window.TTDUniversalPixelApi === 'function') {
              this.pixelApi = new window.TTDUniversalPixelApi();
              this.pixelApi.init(
                this.config.advertiserId,
                this.config.trackingTagIds,
                this.config.trackingUrl!,
              );
              this.initialized = true;
              resolve(true);
            } else {
              logger.error('TTDUniversalPixelApi not available');
              resolve(false);
            }
          } catch (error) {
            logger.error('Failed to initialize TTD Pixel:', error);
            resolve(false);
          }
        });
      });
    } catch (error) {
      logger.error('Failed to load TTD Pixel script:', error);
      return false;
    }
  }

  track(event: TrackingEvent): void {
    if (!this.initialized || !this.pixelApi || typeof window === 'undefined') return;

    try {
      if (this.loggingEnabled) {
        logger.log('TTD Pixel tracking event:', event);
      }

      // Track event with TTD Universal Pixel
      if (event.eventName && event.properties) {
        this.pixelApi.track(event.eventName, event.properties);
      } else if (event.eventName) {
        this.pixelApi.track(event.eventName);
      }
    } catch (error) {
      logger.error('TTD Pixel tracking error:', error);
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  private loadTtdScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const oScript = document.createElement('script');
        oScript.type = 'text/javascript';
        oScript.async = true;
        oScript.src = 'https://js.adsrvr.org/up_loader.1.1.0.js';

        oScript.onload = () => resolve();
        oScript.onerror = () => reject(new Error('Failed to load TTD script'));

        document.head.appendChild(oScript);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * 启用TTD Pixel日志
   */
  enableLog(): void {
    this.loggingEnabled = true;
  }

  /**
   * 禁用TTD Pixel日志
   */
  disableLog(): void {
    this.loggingEnabled = false;
  }

  private waitForTtdReady(callback: () => void): void {
    if (typeof window.ttd_dom_ready === 'function') {
      window.ttd_dom_ready(callback);
    } else {
      // Fallback: wait for script to load and DOM to be ready
      const checkReady = () => {
        if (typeof window.ttd_dom_ready === 'function') {
          window.ttd_dom_ready(callback);
        } else if (document.readyState === 'complete' || document.readyState === 'interactive') {
          // If ttd_dom_ready is not available, use standard DOM ready check
          setTimeout(callback, 100);
        } else {
          setTimeout(checkReady, 100);
        }
      };
      checkReady();
    }
  }
}
